/**
 * Cockpit layout tree — types et invariants.
 *
 * Un Layout est un arbre de LayoutNodes :
 *   - Split    : conteneur row ou column avec N >= 1 enfants, chacun avec une taille en %
 *   - TabGroup : feuille contenant N panels accessibles via tabs
 *
 * Tous les nodes sont des plain objects immuables (readonly partout). Les
 * mutations retournent un nouveau Layout (cf `mutations.ts`).
 *
 * Invariants enforced par `cleanupNode` (cf `normalize.ts`) :
 *   - Une racine existe toujours (Split ou TabGroup non-null).
 *   - Une TabGroup avec 0 panels est retiree (sauf racine, qui devient empty).
 *   - Un Split avec 0 enfants est retire.
 *   - Un Split avec 1 enfant est aplati a son enfant.
 *   - sizes.length === children.length, sizes normalisees a sum 100.
 *   - activeIndex dans [0, panels.length-1] ou -1 si vide.
 */

/** Reference vers une definition de panel dans `PanelRegistry`. */
export type PanelRef = string;

/**
 * Seul "magic id" de panel connu du layout core : le placeholder vide rendu
 * dans une cellule sans contenu (cf `@/panels/empty/EmptyPanel`). Le layout a
 * besoin de le connaitre pour deux comportements :
 *   - `addPanelToGroup` retire les placeholders quand un vrai panel est ajoute
 *     a leur group (l'onglet "Ajouter un panneau" se ferme au lieu de rester
 *     en double).
 *   - la redistribution de template (`redistribute.ts`) ignore les groups qui
 *     ne contiennent QUE des placeholders (ce ne sont pas des "vues actives").
 *
 * DOIT rester aligne sur l'id `"empty"` declare dans `PanelRegistry`.
 */
export const EMPTY_PANEL_ID = "empty" as const;

/** Un onglet a l'interieur d'un TabGroup. */
export interface PanelInstance {
  /** ID unique de cette instance — utilise pour drag/drop et persistence. */
  readonly id: string;
  /** Quel panel rendre (lookup dans la registry). */
  readonly panelId: PanelRef;
  /**
   * Quelle vue du panel rendre. Un meme panel peut avoir plusieurs vues
   * (ex: "list" / "grid" / "analytics"). Si omis, le shell utilise
   * `def.defaultViewId` au render.
   *
   * NB : "view" ici = mode d'affichage interne au panel ; ne pas confondre
   * avec "espace de travail" (workspace, terminologie Phase 4) qui est un
   * preset de layout global.
   */
  readonly viewId?: string;
  /**
   * Parametres specifiques au panel (data scope, filters, etc.). Le layout
   * ne sait rien de leur shape — c'est au panel de la valider/typer.
   * Permet d'avoir 2 instances du meme panelId/viewId mais avec des donnees
   * differentes (ex: chats du creator A vs creator B).
   */
  readonly params?: Readonly<Record<string, unknown>>;
  /** Override optionnel du titre (defaut = registry def title). */
  readonly title?: string;
}

/** Direction d'un Split. row = horizontal (enfants gauche/droite), column = vertical. */
export type SplitDirection = "row" | "column";

/** Conteneur de splits. N >= 1 enfants. */
export interface Split {
  readonly kind: "split";
  readonly id: string;
  readonly direction: SplitDirection;
  readonly children: readonly LayoutNode[];
  /** Tailles en pourcent, sum normalisee a 100. length === children.length. */
  readonly sizes: readonly number[];
}

/** Feuille groupant N panels accessibles via tabs. */
export interface TabGroup {
  readonly kind: "tabs";
  readonly id: string;
  readonly panels: readonly PanelInstance[];
  /** Index du panel actif dans [0, panels.length-1], ou -1 si vide. */
  readonly activeIndex: number;
}

export type LayoutNode = Split | TabGroup;

/** Wrapper top-level du layout. */
export interface Layout {
  /** Version du schema — bump quand on casse la shape. */
  readonly version: number;
  /** Racine de l'arbre. Toujours presente, peut etre une TabGroup vide. */
  readonly root: LayoutNode;
}

/** Bord d'un node ou un Split est cree. */
export type Edge = "left" | "right" | "top" | "bottom";

/** Specification ou ajouter un nouveau panel via `addPanel`. */
export type AddTarget =
  /** Ajouter au TabGroup actif (heuristique : premier non-vide en DFS). */
  | { kind: "active-group" }
  /** Ajouter a un TabGroup specifique. */
  | { kind: "group"; groupId: string }
  /**
   * Creer un nouveau Split a partir d'un edge d'un node existant.
   *
   * `sizes` (optionnel) = proportions initiales `[targetShare, newSiblingShare]`
   * en %, dans l'ordre logique target-puis-nouveau (l'ordre reel des enfants
   * est gere par l'edge). Si omis, le split est 50/50. Normalisees a 100.
   * Utilise pour ouvrir un layout avec des panneaux de tailles inegales (ex:
   * chat plus large que fan-info a l'ouverture d'une conversation).
   */
  | {
      kind: "split-edge";
      targetNodeId: string;
      edge: Edge;
      sizes?: readonly [number, number];
    };

/** Version actuelle du schema serialise. */
export const LAYOUT_SCHEMA_VERSION = 1 as const;
